'use client';

import {
  Checkbox,
  CircularProgress,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { stickNameCell, StyledHeaderTableCell, StyledTable } from '~/shared';
import { CampaignReport } from '~/types';

import { useGetSponsoredProductsReportsCampaigns } from '../../hooks';

import { SponsoredProductsReportCampaignsRow } from './Row';

export type SponsoredProductsReportsCampaignsResponse = {
  list?: CampaignReport[];
  total: number;
};

export type SponsoredProductsReportCampaignsListProps = {
  getReportsCampaignsQuery: ReturnType<
    typeof useGetSponsoredProductsReportsCampaigns
  >;
  advertiserId: string;
  selectedCampaigns: string[];
  onSelectCampaign: (campaignId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
};

export function SponsoredProductsReportCampaignsList({
  getReportsCampaignsQuery,
  advertiserId,
  selectedCampaigns,
  onSelectCampaign,
  onSelectAll,
}: SponsoredProductsReportCampaignsListProps) {
  const { data, isLoading, isError, error } = getReportsCampaignsQuery;

  const campaigns = data?.list || [];
  const allSelected =
    campaigns.length > 0 &&
    campaigns.every((campaign: CampaignReport) =>
      selectedCampaigns.includes(campaign.idCampaign)
    );
  const someSelected = selectedCampaigns.length > 0 && !allSelected;

  const handleSelectAll = (checked: boolean) => {
    onSelectAll(checked);
  };

  return (
    <StyledTable aria-label="Tabela de Campanhas de Produtos Patrocinados">
      <TableHead>
        <TableRow>
          <StyledHeaderTableCell align="center">
            <Checkbox
              indeterminate={someSelected}
              checked={allSelected}
              onChange={e => handleSelectAll(e.target.checked)}
              disabled={isLoading || campaigns.length === 0}
            />
          </StyledHeaderTableCell>
          <StyledHeaderTableCell sx={{ width: '60px' }} align="center">
            ID
          </StyledHeaderTableCell>
          <StyledHeaderTableCell sx={stickNameCell()}>
            Nome da Campanha
          </StyledHeaderTableCell>
          <StyledHeaderTableCell>Plataforma</StyledHeaderTableCell>
          <StyledHeaderTableCell>Impressões</StyledHeaderTableCell>
          <StyledHeaderTableCell>Cliques</StyledHeaderTableCell>
          <StyledHeaderTableCell>CTR</StyledHeaderTableCell>
          <StyledHeaderTableCell>CPM</StyledHeaderTableCell>
          <StyledHeaderTableCell>CPC</StyledHeaderTableCell>
          <StyledHeaderTableCell>Vendas</StyledHeaderTableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {isLoading && (
          <TableRow>
            <TableCell component="th" scope="row" colSpan={10} align="center">
              <CircularProgress size={15} /> Carregando
            </TableCell>
          </TableRow>
        )}

        {!isLoading && !isError && data?.list?.length === 0 && (
          <TableRow>
            <TableCell colSpan={10} align="center">
              Sem resultados
            </TableCell>
          </TableRow>
        )}

        {isError && (
          <TableRow>
            <TableCell
              sx={{ color: theme => theme.palette.error.light }}
              colSpan={10}
              align="center"
            >
              <Typography>Ocorreu um erro: {error?.name}</Typography>
              <Typography>{error?.message}</Typography>
            </TableCell>
          </TableRow>
        )}

        {!isLoading &&
          !isError &&
          data?.list?.map((resume: CampaignReport) => {
            return (
              <SponsoredProductsReportCampaignsRow
                key={resume.idCampaign}
                resume={resume}
                advertiserId={advertiserId}
                isSelected={selectedCampaigns.includes(resume.idCampaign)}
                onSelect={(selected: boolean) =>
                  onSelectCampaign(resume.idCampaign, selected)
                }
              />
            );
          })}
      </TableBody>
    </StyledTable>
  );
}
